import { FC, useState } from "react";
import { useTranslation } from "next-i18next";
import { useEffectOnce } from "react-use";
import Link from "next/link";
import Image from "next/image";
import useBrowserRoutes from "@/src/utils/helpers/useBrowserRoutes";
import { Article } from "@/lib/types/Article";
import { useHomePageContext } from "@/src/features/home/HomePageContext";
import { useAppContext } from "@/src/config/AppProvider";
import DOMPurify from "isomorphic-dompurify";

const ArticlesSection = () => {
  const { t } = useTranslation();
  const { getLocalization } = useAppContext();
  const { articles } = useHomePageContext();

  return (
    <div className="rbt-course-area bg-color-white rbt-section-gap">
      <div className="container">
        <div className="row mb--60">
          <div className="col-lg-12">
            <div className="section-title text-center">
              <span className="subtitle bg-pink-opacity">{t("articles")}</span>
              <h2 className="title">{getLocalization("newArticlesSlogan")}</h2>
            </div>
          </div>
        </div>
        <div className="row g-5">
          {articles.map(item => (
            <ArticleItem key={item.IdGuid} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ArticleItem: FC<{ item: Article }> = ({ item }) => {
  const routes = useBrowserRoutes();
  const { t } = useTranslation();
  const [sanitizedContent, setSanitizedContent] = useState<string>("");
  const detailLink = routes.articleDetail.replace(":slug", item.Slug);

  useEffectOnce(() => {
    setSanitizedContent(DOMPurify.sanitize(item.Content, { USE_PROFILES: { html: false } }));
  });

  return (
    <div className="col-lg-4 col-md-6 col-12" key={item.IdGuid}>
      <div className="rbt-card variation-01 rbt-hover">
        <div className="rbt-card-img">
          <Link href={detailLink}>
            <Image
              src={item.ThumbImageUrl}
              width={355}
              height={244}
              alt={item.ImageAltText}
              style={{
                objectFit: "cover",
                height: 244
              }}
            />
          </Link>
        </div>
        <div className="rbt-card-body">
          <ul className="rbt-meta">
            <li>{item.Category.Name}</li>
          </ul>
          <h4 className="rbt-card-title">
            <Link href={detailLink}>{item.Title}</Link>
          </h4>
          <p className="rbt-card-text text-multiline-ellipsis-4" dangerouslySetInnerHTML={{ __html: sanitizedContent }}></p>
          <div className="rbt-card-bottom">
            <Link className="rbt-btn-link" href={detailLink}>
              {t("articleDetails")}
              <i className="feather-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlesSection;
